<!-- ADR 47 F3 — HMI alarm annunciator widget. A design/screen/kiosk element that
     shows the live count of active alarms (optionally for one area) + the highest
     priority, blinking while there are unacknowledged criticals. Self-resolving:
     in the portal it calls list_active_alarms; in a tokenized kiosk it reads the
     token-authed /api/embed/alarm-summary. Click → the Alarms page (portal). -->
<template>
  <div class="alarm-banner" :class="{ blink: live && unacked > 0 && highest === 'critical', clickable: !inKiosk }"
       :style="boxStyle" @click="open">
    <Siren class="ab-ico" :style="{ color: accent }" />
    <div class="ab-body">
      <div class="ab-count" :style="{ color: accent }">{{ live ? unacked : 0 }}</div>
      <div class="ab-label">{{ labelText }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Siren } from 'lucide-vue-next';
import { callTool } from '../api/client';

const props = defineProps<{ p: Record<string, unknown>; live?: boolean }>();
const live = computed(() => props.live === true);
const area = computed(() => (typeof props.p.area === 'string' && props.p.area ? props.p.area : ''));

const route = useRoute();
const router = useRouter();
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
// A kiosk renders /screen/...?token=<jwt> — use the token-authed embed endpoint.
const kioskToken = computed(() => (typeof route.query.token === 'string' ? route.query.token : ''));
const inKiosk = computed(() => Boolean(kioskToken.value));

const unacked = ref(0);
const total = ref(0);
const highest = ref<string>('');

const PRIO_COLOR: Record<string, string> = { critical: '#dc2626', high: '#ea580c', medium: '#2c70b9', low: '#64748b', diagnostic: '#94a3b8' };
const accent = computed(() => (live.value && unacked.value > 0 ? (PRIO_COLOR[highest.value] ?? '#dc2626') : '#16a34a'));
const labelText = computed(() => {
  if (!live.value) return area.value || 'Alarmas';
  if (!unacked.value) return area.value ? `${area.value} · OK` : 'Sin alarmas';
  return `${unacked.value === 1 ? 'alarma' : 'alarmas'}${highest.value ? ' · ' + highest.value : ''}`;
});
const boxStyle = computed(() => ({
  borderColor: live.value && unacked.value > 0 ? accent.value : 'var(--color-line)',
}));

function apply(d: { unacked?: number; total?: number; highest?: string | null }): void {
  unacked.value = d.unacked ?? 0;
  total.value = d.total ?? 0;
  highest.value = d.highest ?? '';
}

async function load(): Promise<void> {
  if (!live.value) return;
  try {
    if (inKiosk.value) {
      const q = `token=${encodeURIComponent(kioskToken.value)}${area.value ? `&area=${encodeURIComponent(area.value)}` : ''}`;
      const r = await fetch(`${API_BASE}/api/embed/alarm-summary?${q}`);
      if (r.ok) apply(await r.json());
    } else {
      const d = await callTool<{ alarms?: Array<{ state: string; priority: string }> }>('list_active_alarms', { state: 'active', ...(area.value ? { area: area.value } : {}) });
      const alarms = d?.alarms ?? [];
      const un = alarms.filter((a) => a.state === 'unack_active' || a.state === 'rtn_unack');
      const order = ['critical', 'high', 'medium', 'low', 'diagnostic'];
      apply({ unacked: un.length, total: alarms.length, highest: order.find((p) => un.some((a) => a.priority === p)) ?? null });
    }
  } catch { /* best-effort */ }
}

function open(): void { if (!inKiosk.value) void router.push('/alarms'); }

let timer: ReturnType<typeof setInterval> | null = null;
onMounted(() => { void load(); if (live.value) timer = setInterval(load, 5000); });
onBeforeUnmount(() => { if (timer) clearInterval(timer); });
</script>

<style scoped>
.alarm-banner { display: flex; align-items: center; gap: 10px; width: 100%; height: 100%; box-sizing: border-box; padding: 8px 12px; border: 1.5px solid var(--color-line); border-radius: 10px; background: var(--color-surface); overflow: hidden; }
.alarm-banner.clickable { cursor: pointer; }
.ab-ico { width: 26px; height: 26px; flex-shrink: 0; }
.ab-body { min-width: 0; line-height: 1.1; }
.ab-count { font-size: 26px; font-weight: 800; }
.ab-label { font-size: 12px; color: var(--color-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.alarm-banner.blink { animation: ab-blink 1s steps(1) infinite; }
@keyframes ab-blink { 50% { background: #fef2f2; } }
</style>
